import {
  CLASSIFICATION_SOURCES,
  CONVERSATION_KINDS,
  INTENTS,
  type ClassificationSource,
  type ConversationKind,
  type Intent,
} from '@rp/contracts'
import { useEffect, useRef, useState } from 'react'
import { Field } from '../../design'
import { QUEUE_COPY } from './copy'
import { CLASSIFICATION_SOURCE_LABELS, INTENT_LABELS, KIND_LABELS } from './labels'
import type { QueueFilter } from './queries'

const AUTHOR_DEBOUNCE_MS = 400

const withIntent = (filter: QueueFilter, intent: Intent | undefined): QueueFilter => {
  const { intent: _current, ...rest } = filter
  return intent === undefined ? { ...rest, page: 1 } : { ...rest, intent, page: 1 }
}

const withKind = (filter: QueueFilter, kind: ConversationKind | undefined): QueueFilter => {
  const { kind: _current, ...rest } = filter
  return kind === undefined ? { ...rest, page: 1 } : { ...rest, kind, page: 1 }
}

const withSource = (filter: QueueFilter, source: ClassificationSource | undefined): QueueFilter => {
  const { source: _current, ...rest } = filter
  return source === undefined ? { ...rest, page: 1 } : { ...rest, source, page: 1 }
}

const withAuthor = (filter: QueueFilter, author: string | undefined): QueueFilter => {
  const { author: _current, ...rest } = filter
  return author === undefined || author === '' ? { ...rest, page: 1 } : { ...rest, author, page: 1 }
}

const AuthorField = ({ filter, onChange }: { filter: QueueFilter; onChange: (next: QueueFilter) => void }) => {
  const [draft, setDraft] = useState(filter.author ?? '')
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const filterRef = useRef(filter)
  filterRef.current = filter

  useEffect(() => {
    const id = setTimeout(() => {
      if (draft === (filterRef.current.author ?? '')) return
      onChangeRef.current(withAuthor(filterRef.current, draft))
    }, AUTHOR_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [draft])

  return (
    <Field id="queue-author" label={QUEUE_COPY.filterAuthor}>
      {({ id }) => <input id={id} type="text" value={draft} onChange={(event) => setDraft(event.target.value)} />}
    </Field>
  )
}

export const QueueFilters = ({ filter, onChange }: { filter: QueueFilter; onChange: (next: QueueFilter) => void }) => (
  <div className="queue-filters">
    <Field id="queue-intent" label={QUEUE_COPY.filterIntent}>
      {({ id }) => (
        <select
          id={id}
          value={filter.intent ?? ''}
          onChange={(event) =>
            onChange(withIntent(filter, event.target.value === '' ? undefined : (event.target.value as Intent)))
          }
        >
          <option value="">{QUEUE_COPY.filterAll}</option>
          {INTENTS.map((intent) => (
            <option key={intent} value={intent}>
              {INTENT_LABELS[intent]}
            </option>
          ))}
        </select>
      )}
    </Field>
    <Field id="queue-kind" label={QUEUE_COPY.filterKind}>
      {({ id }) => (
        <select
          id={id}
          value={filter.kind ?? ''}
          onChange={(event) =>
            onChange(withKind(filter, event.target.value === '' ? undefined : (event.target.value as ConversationKind)))
          }
        >
          <option value="">{QUEUE_COPY.filterAll}</option>
          {CONVERSATION_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {KIND_LABELS[kind]}
            </option>
          ))}
        </select>
      )}
    </Field>
    {filter.folder === 'spam' ? (
      <>
        <Field id="queue-source" label={QUEUE_COPY.filterSource}>
          {({ id }) => (
            <select
              id={id}
              value={filter.source ?? ''}
              onChange={(event) =>
                onChange(withSource(filter, event.target.value === '' ? undefined : (event.target.value as ClassificationSource)))
              }
            >
              <option value="">{QUEUE_COPY.filterAll}</option>
              {CLASSIFICATION_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {CLASSIFICATION_SOURCE_LABELS[source]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <AuthorField filter={filter} onChange={onChange} />
      </>
    ) : null}
  </div>
)
